import {computed, ref} from 'vue';
import {createError, useAsyncData} from '#imports';
import {useErrorHandler} from '@malt/utils';
import type {CompanyLegalAgreementContentResponse, LegalAgreementContentResponse} from '@api';
import {FetchError} from '@api';

interface LegalAgreementContent extends LegalAgreementContentResponse {
    variableValues?: CompanyLegalAgreementContentResponse['variableValues'];
}

export function useLegalAgreementContent() {
    const {handleFatalError} = useErrorHandler();
    const legalAgreementContentResponse = ref<LegalAgreementContent | null>(null);

    async function fetchContent(key: string, loadContent: () => Promise<LegalAgreementContent>) {
        legalAgreementContentResponse.value = null;

        const {data, error} = await useAsyncData(
            key,
            async () => {
                try {
                    return await loadContent();
                } catch (error) {
                    if (error instanceof FetchError) {
                        const errors = error.getCauseData();
                        if (Array.isArray(errors) && errors.length > 0 && errors.every((item) => typeof item?.error === 'string')) {
                            throw createError({message: errors.map((item) => item.error).join('\n'), statusCode: error.statusCode ?? 500});
                        }
                    }
                    throw error;
                }
            },
            {
                deep: true,
            },
        );

        if (error.value) {
            handleFatalError(error.value.message, error.value.statusCode ?? 500);
            return;
        }

        if (data.value) {
            legalAgreementContentResponse.value = data.value;
        }
    }

    const legalAgreementContent = computed<string>(() => legalAgreementContentResponse.value?.content ?? '');
    const variableValues = computed(() => legalAgreementContentResponse.value?.variableValues);

    return {
        legalAgreementContent,
        variableValues,

        fetchContent,
    };
}
